<template>
  <div class="canceled-page">
    <div class="canceled-card">
      <div class="signup-stepper">
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Account</div>
        </div>
        <div class="signup-step-line signup-step-line-done"></div>
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Market</div>
        </div>
        <div class="signup-step-line signup-step-line-done"></div>
        <div class="signup-step signup-step-active">
          <div class="signup-step-circle">3</div>
          <div class="signup-step-label">Billing</div>
        </div>
      </div>

      <div class="canceled-icon" aria-hidden="true">&times;</div>
      <h1 class="canceled-title">Checkout was cancelled</h1>
      <p class="canceled-body">
        No charge was made and your card was not stored. Your market territory is still held
        for you and is not yet active &mdash; you can come back and finish anytime.
      </p>

      <!-- Help cluster — make support contact + ToS easy to find. People
           who back out of checkout often have a question; surface the
           answer surfaces directly here so they don't have to dig. -->
      <ul class="help-list">
        <li>
          <strong>Need help?</strong>
          Email <a href="mailto:support@kiey.com">support@kiey.com</a> and we&rsquo;ll get back
          to you within one business day.
        </li>
        <li>
          <strong>Have a question about pricing?</strong>
          The full breakdown is on the previous step &mdash; click Try again to review it.
        </li>
        <li>
          <strong>Want to read the terms first?</strong>
          See the <a href="/terms_and_conditions" target="_blank" rel="noopener">Terms of Service</a>
          for the complete subscription, cancellation, and refund policy.
        </li>
      </ul>

      <p v-if="error_message" class="canceled-error">{{ error_message }}</p>

      <div class="canceled-actions">
        <button
          type="button"
          class="canceled-btn canceled-btn-primary"
          :disabled="retrying"
          @click="retryCheckout"
        >
          {{ retrying ? 'Opening checkout&hellip;' : 'Try again' }}
        </button>
        <router-link to="/sign_up/billing" class="canceled-btn canceled-btn-secondary">
          Back to subscribe page
        </router-link>
      </div>

      <p class="canceled-fineprint">
        Your market territory remains reserved while we wait for you to subscribe. If you don&rsquo;t
        finish in 30 days it returns to the public availability pool.
      </p>
    </div>
  </div>
</template>

<script>
// Versioning lets us prove later — for any specific tenant on any specific
// day — exactly which billing-terms text they accepted. Bump in lockstep
// with billing_coming_soon.vue so retries from this page record the same
// version the user originally saw.
const BILLING_TERMS_VERSION = '2026-05-03';

export default {
  name: 'BillingCanceled',
  data() {
    return {
      retrying: false,
      error_message: '',
    };
  },
  methods: {
    async retryCheckout() {
      this.error_message = '';
      this.retrying = true;
      try {
        // Sympathetic path: user already accepted terms on the previous
        // step. Re-pass the same version so the server records the same
        // acceptance trail for this retry; if the version has bumped
        // since, the server will reject and we'll route them back to the
        // subscribe page to re-read.
        const result = await this.$store.dispatch('Market_billing/checkout', {
          skip_claim: true,
          billing_terms_accepted: true,
          billing_terms_version: BILLING_TERMS_VERSION,
        });
        if (result && result.success && result.url) {
          window.location.href = result.url;
          return;
        }
        this.error_message =
          (result && result.message) ||
          "We couldn't reach our payment provider. Please try again or contact support.";
      } catch (err) {
        console.error('[billing_canceled] retry failed:', err);
        this.error_message =
          "We couldn't reach our payment provider. Please try again or contact support.";
      } finally {
        this.retrying = false;
      }
    },
  },
};
</script>

<style scoped>
.canceled-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: #f5f7fb;
}
.canceled-card {
  width: 100%;
  max-width: 620px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 48px 40px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.08);
  box-sizing: border-box;
}

/* Stepper */
.signup-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
  max-width: 360px;
}
.signup-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 88px;
}
.signup-step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e4e8f1;
  color: #5a6478;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.signup-step-active .signup-step-circle { background: #061d47; color: #fff; }
.signup-step-done .signup-step-circle { background: #19a99d; color: #fff; }
.signup-step-label {
  font-size: 11px;
  color: #5a6478;
  margin-top: 4px;
  letter-spacing: 0.3px;
  font-weight: 600;
  text-transform: uppercase;
}
.signup-step-active .signup-step-label { color: #061d47; }
.signup-step-done .signup-step-label { color: #19a99d; }
.signup-step-line {
  flex: 0 1 80px;
  height: 2px;
  background: #e4e8f1;
  margin: 0 6px 18px;
}
.signup-step-line-done { background: #19a99d; }

.canceled-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fdecec;
  color: #c0392b;
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
  margin: 8px auto 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.canceled-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 14px 0;
}
.canceled-body {
  color: #4a4a4a;
  font-family: Arial, sans-serif;
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 22px 0;
}

/* Help list */
.help-list {
  list-style: none;
  padding: 0;
  margin: 0 auto 22px;
  text-align: left;
  max-width: 480px;
  background: #fafbfd;
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 16px 20px;
}
.help-list li {
  padding: 8px 0;
  color: #2d2d2d;
  font-size: 13px;
  line-height: 1.55;
}
.help-list li + li {
  border-top: 1px solid #f0f2f6;
}
.help-list strong {
  color: #061d47;
  display: block;
  margin-bottom: 2px;
}
.help-list a {
  color: #29abe2;
  text-decoration: none;
}
.help-list a:hover { text-decoration: underline; }

.canceled-error {
  color: #c0392b;
  font-family: Arial, sans-serif;
  font-size: 13px;
  margin: 0 0 16px 0;
}

.canceled-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 18px;
}
.canceled-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 28px;
  border-radius: 24px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
  min-width: 200px;
}
.canceled-btn-primary { background: #29abe2; color: #ffffff; }
.canceled-btn-primary:hover:not(:disabled) { background: #1f9bd0; }
.canceled-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.canceled-btn-secondary {
  background: #ffffff;
  color: #061d47;
  border-color: #061d47;
}
.canceled-btn-secondary:hover {
  background: #061d47;
  color: #ffffff;
}

.canceled-fineprint {
  color: #6a7388;
  font-size: 12px;
  line-height: 1.5;
  margin: 12px 0 0;
}

@media (max-width: 600px) {
  .canceled-card { padding: 32px 20px; }
  .signup-stepper { max-width: 280px; margin-bottom: 24px; }
  .signup-step { min-width: 76px; }
  .signup-step-line { flex: 0 1 60px; }
  .signup-step-label { font-size: 10px; }
  .canceled-title { font-size: 22px; }
  .canceled-actions { flex-direction: column; }
  .canceled-btn { width: 100%; min-width: 0; }
  .help-list { padding: 12px 14px; }
}
</style>
